import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { buildIlikeOr } from "@/lib/postgrest";
import { parseTitle } from "./youtube-parser";
import { classifyVideo, matchesSyncCategories, categoryLabel } from "./sync-categories";
import { DiscardTally, mergeDiscardStats, auditSyncRun, type AuditVerdict } from "./sync-audit";
import { matchesTeachingKeyword, requiresTeachingChecks, keywordsLabel } from "./sync-eligibility";

import { findParashaInText } from "./parasha-match";
import { detectFestivitySlug, extractTopicLabel } from "./festivity-match";
import { logActivity } from "./activity-log.server";
import { notifySupportOfError } from "./email.server";

/** Retorna true se a data ISO cai num sábado no fuso America/Sao_Paulo. */
function isSaturdayInSaoPaulo(isoDate?: string | null): boolean {
  if (!isoDate) return false;
  try {
    const d = new Date(isoDate);
    const weekday = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Sao_Paulo",
      weekday: "short",
    }).format(d);
    return weekday === "Sat";
  } catch {
    return false;
  }
}

/** Retorna YYYY-MM-DD no fuso America/Sao_Paulo para uma data ISO. */
function toSaoPauloDate(isoDate?: string | null): string | null {
  if (!isoDate) return null;
  try {
    const d = new Date(isoDate);
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return y && m && day ? `${y}-${m}-${day}` : null;
  } catch {
    return null;
  }
}

/** Formata YYYY-MM-DD -> DD/MM/YYYY. */
function formatDateBR(ymd?: string | null): string | null {
  if (!ymd) return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

async function ensureAdmin(context: any) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Acesso negado");
}

export const getCurrentUserRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    return { isAdmin: !!data };
  });

/**
 * Situação e autorização do próprio usuário. Autenticação NÃO concede acesso:
 * é preciso existir cadastro de membro aprovado e sem bloqueios.
 */
export const getMyAccessStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const email = (context.claims as any)?.email as string | undefined;

    // Sincronização ao entrar: só é feita quando o perfil nunca foi sincronizado
    // (primeiro acesso) e com limite de tempo curto, para que a abertura da área
    // de membros nunca fique presa esperando a Gestão. Nos acessos seguintes a
    // atualização fica com o cron e com "Atualizar dados agora".
    // Falha na Gestão nunca bloqueia o acesso já concedido.
    if (email) {
      try {
        const { data: row } = await context.supabase
          .from("profiles")
          .select("external_synced_at")
          .eq("id", context.userId)
          .maybeSingle();
        const neverSynced =
          !row?.external_synced_at || !Number.isFinite(Date.parse(row.external_synced_at));
        if (neverSynced) {
          const { syncMemberByEmail } = await import("./member-sync.server");
          await Promise.race([
            syncMemberByEmail(email),
            new Promise((_r, reject) =>
              setTimeout(() => reject(new Error("Gestão não respondeu em 5s")), 5000),
            ),
          ]);
        }
      } catch (e: any) {
        const { logSyncEvent } = await import("./member-sync.server");
        await logSyncEvent({
          direction: "inbound",
          action: "member.sync.on_access",
          userId: context.userId,
          email,
          status: "error",
          message: e?.message ?? String(e),
        });
      }
    }

    const { resolveMembershipAccess } = await import("./membership.server");
    const access = await resolveMembershipAccess(context.supabase, context.userId);

    return {
      approved: access.allowed,
      status: access.state,
      hasProfile: access.hasProfile,
      isAdmin: access.isAdmin,
      fullName: access.fullName,
      cpf: access.cpf,
      missing: access.missing,
      blockReason: access.blockReason,
      blockDetail: access.blockDetail,
    };
  });

const CHANNEL_HANDLE = "@rechovot";

async function resolveChannelUploadsPlaylist(
  apiKey: string,
): Promise<{ channelId: string; uploadsId: string }> {
  const { youtubeList } = await import("./youtube-api.server");
  const j = await youtubeList<{
    items?: Array<{ id?: string; contentDetails?: { relatedPlaylists?: { uploads?: string } } }>;
  }>("channels", { part: "contentDetails", forHandle: CHANNEL_HANDLE }, { apiKey });
  const item = j.items?.[0];
  const channelId = item?.id;
  const uploadsId = item?.contentDetails?.relatedPlaylists?.uploads;
  if (!channelId || !uploadsId)
    throw new Error("Canal @rechovot: playlist de uploads não encontrada");
  return { channelId, uploadsId };
}

/** Busca UMA página (50) de vídeos da playlist de uploads. */
async function fetchOneUploadsPage(apiKey: string, uploadsId: string, pageToken?: string | null) {
  const cutoff = Date.now() - 20 * 365.25 * 24 * 60 * 60 * 1000;
  const { youtubeList } = await import("./youtube-api.server");
  const j = await youtubeList<{
    items?: Array<{
      contentDetails?: { videoId?: string; videoPublishedAt?: string };
      snippet?: { resourceId?: { videoId?: string }; publishedAt?: string };
    }>;
    nextPageToken?: string;
  }>(
    "playlistItems",
    {
      part: "snippet,contentDetails",
      playlistId: uploadsId,
      maxResults: "50",
      pageToken: pageToken ?? undefined,
    },
    { apiKey },
  );

  let reachedCutoff = false;
  const items: { videoId: string }[] = [];
  for (const it of j.items ?? []) {
    const videoId = it.contentDetails?.videoId ?? it.snippet?.resourceId?.videoId;
    if (!videoId) continue;
    const publishedAt = it.contentDetails?.videoPublishedAt ?? it.snippet?.publishedAt;
    if (publishedAt && new Date(publishedAt).getTime() < cutoff) {
      reachedCutoff = true;
      continue;
    }
    items.push({ videoId });
  }
  const nextToken = reachedCutoff ? null : (j.nextPageToken ?? null);
  return { items, nextToken };
}

/** Enriquecer 1 lote (até 50 IDs) com snippet + liveStreamingDetails; filtra só lives encerradas. */
async function fetchVideoDetailsBatch(apiKey: string, ids: string[]) {
  if (ids.length === 0) return [];
  const { youtubeList } = await import("./youtube-api.server");
  const j = await youtubeList<{
    items?: Array<{
      id: string;
      snippet?: unknown;
      liveStreamingDetails?: { actualEndTime?: string };
    }>;
  }>("videos", { part: "snippet,liveStreamingDetails", id: ids.join(",") }, { apiKey });
  const out: any[] = [];
  for (const it of j.items ?? []) {
    if (!it.liveStreamingDetails?.actualEndTime) continue;
    out.push({
      contentDetails: { videoId: it.id },
      snippet: it.snippet,
      liveStreamingDetails: it.liveStreamingDetails,
    });
  }
  return out;
}

const syncSchema = z.object({
  maxPages: z.number().int().min(1).max(200).default(3),
  categories: z.array(z.string().max(60)).max(40).optional(),
});

/**
 * Inicia uma sincronização em lotes: cria o run, resolve a playlist de uploads,
 * marca runs travados como falhos e retorna o runId. O processamento em si é
 * feito por chamadas sucessivas a `runYoutubeSyncBatch`, para não estourar o
 * limite de tempo de execução de cada requisição.
 */
export const startYoutubeSync = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { maxPages?: number; categories?: string[] }) =>
    syncSchema.parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);

    const { getYoutubeApiKey } = await import("./platform-secrets.server");
    const apiKey = await getYoutubeApiKey();
    if (!apiKey) throw new Error("Chave da API do YouTube não configurada.");

    const { getYoutubeQuotaBudget } = await import("./youtube-api.server");
    const budget = await getYoutubeQuotaBudget();
    if (!budget.canCall) {
      throw new Error(
        "Cota diária da API do YouTube esgotada. A sincronização volta automaticamente após a meia-noite no horário do Pacífico.",
      );
    }

    // Fecha runs travados (>5min sem progresso) para não deixarem a UI presa.
    await context.supabase
      .from("sync_runs")
      .update({
        status: "failed",
        finished_at: new Date().toISOString(),
        error_message: "Execução interrompida (timeout). Nova sincronização em lotes iniciada.",
      })
      .eq("status", "running")
      .lt("last_progress_at", new Date(Date.now() - 5 * 60 * 1000).toISOString());

    const { uploadsId } = await resolveChannelUploadsPlaylist(apiKey);

    const { data: run, error: runErr } = await context.supabase
      .from("sync_runs")
      .insert({
        max_pages: data.maxPages,
        category_filters: data.categories && data.categories.length > 0 ? data.categories : null,
        triggered_by: context.userId,
        status: "running",
        uploads_playlist_id: uploadsId,
        phase: "processing",
        last_progress_at: new Date().toISOString(),
      })
      .select()
      .single();
    if (runErr || !run)
      throw runErr ?? new Error("Não foi possível criar o registro de sincronização");
    return { runId: run.id as string };
  });

const batchSchema = z.object({ runId: z.string().uuid() });

/**
 * Processa UM lote: 1 página da playlist de uploads (50 vídeos), enriquece com
 * videos.list (snippet+liveStreamingDetails) e faz upsert dos que são lives encerradas.
 * Retorna { done: boolean } — o cliente chama em loop até done=true.
 */
export const runYoutubeSyncBatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { runId: string }) => batchSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);

    const { getYoutubeApiKey } = await import("./platform-secrets.server");
    const apiKey = await getYoutubeApiKey();
    if (!apiKey) throw new Error("Chave da API do YouTube não configurada.");

    const { data: run, error: runErr } = await context.supabase
      .from("sync_runs")
      .select("*")
      .eq("id", data.runId)
      .maybeSingle();
    if (runErr || !run) throw runErr ?? new Error("Run não encontrada.");
    if (run.status !== "running") return { done: true, run };

    const finalize = async (patch: Record<string, unknown>) => {
      await context.supabase
        .from("sync_runs")
        .update({
          ...patch,
          finished_at: new Date().toISOString(),
          last_progress_at: new Date().toISOString(),
        })
        .eq("id", data.runId);
    };

    try {
      const uploadsId = run.uploads_playlist_id as string | null;
      if (!uploadsId) throw new Error("Playlist de uploads não resolvida para esta run.");

      // Se já atingiu o teto de páginas ou chegou ao fim da playlist na chamada anterior.
      if (run.pages_fetched >= run.max_pages) {
        await finishRun(context, data.runId, run, {
          stop: true,
          code: "max_pages",
          severity: "info",
          message: "Auditoria: limite de páginas configurado alcançado.",
        });
        return { done: true };
      }

      const { items, nextToken } = await fetchOneUploadsPage(
        apiKey,
        uploadsId,
        run.next_page_token,
      );
      const enriched = await fetchVideoDetailsBatch(
        apiKey,
        items.map((i) => i.videoId),
      );

      const { data: parashiotList } = await context.supabase
        .from("parashiot")
        .select("id, number, name_translit, name_pt");
      const parashaByNumber = new Map<number, { id: string; label: string }>();
      const parashaByName = new Map<string, { id: string; label: string }>();
      (parashiotList ?? []).forEach((p: any) => {
        const entry = { id: p.id as string, label: p.name_translit as string };
        parashaByNumber.set(p.number, entry);
        parashaByName.set((p.name_translit as string).toUpperCase(), entry);
        parashaByName.set((p.name_pt as string).toUpperCase(), entry);
      });

      const { data: festivitiesList } = await context.supabase
        .from("festivities")
        .select("id, slug, name_pt");
      const festivityBySlug = new Map<string, string>();
      const festivityNameBySlug = new Map<string, string>();
      (festivitiesList ?? []).forEach((f: any) => {
        festivityBySlug.set(f.slug, f.id);
        festivityNameBySlug.set(f.slug, f.name_pt);
      });

      // Regras de classificação personalizadas definidas pelo admin no painel.
      const { data: customRules } = await context.supabase
        .from("classification_rules")
        .select("*")
        .eq("enabled", true)
        .order("priority", { ascending: true });

      // Regras de importação (palavras-chave, sábado, festividades) do painel.
      const { loadImportRules } = await import("./catalog-audit.server");
      const importRules = await loadImportRules(context.supabase);

      let inserted = 0,
        updated = 0,
        skipped = 0,
        failed = 0;
      const tally = new DiscardTally();
      // Vídeos da página que não são lives encerradas contam como skipped.
      const enrichedIds = new Set(enriched.map((v: any) => v.contentDetails.videoId));
      const skippedNotLive = items.filter((i) => !enrichedIds.has(i.videoId)).length;
      for (let i = 0; i < skippedNotLive; i++) tally.add("not_live", "nao_live");

      for (const v of enriched) {
        const videoId = v.contentDetails.videoId as string;
        const originalTitle = v.snippet?.title ?? "";
        const baseLog = {
          run_id: data.runId,
          youtube_video_id: videoId,
          title: originalTitle,
          processed_at: new Date().toISOString(),
        };
        try {
          const parsed = parseTitle(originalTitle);
          const rawDescription = (v.snippet?.description ?? "") as string;
          const cls = classifyVideo(originalTitle, rawDescription, (customRules ?? []) as any);
          const categoryFilters = (run.category_filters as string[] | null) ?? null;
          const hasFilters = !!categoryFilters && categoryFilters.length > 0;
          const details = {
            actualStartTime: v.liveStreamingDetails?.actualStartTime,
            actualEndTime: v.liveStreamingDetails?.actualEndTime,
          };
          const effectiveDate =
            parsed.teachingDate ||
            toSaoPauloDate(details.actualStartTime) ||
            toSaoPauloDate(v.snippet?.publishedAt);

          // Filtro por categoria escolhido pelo administrador.
          if (hasFilters && !matchesSyncCategories(categoryFilters, cls)) {
            skipped++;
            tally.add("category_filter", cls.primary);
            await context.supabase.from("sync_items").insert({
              ...baseLog,
              status: "skipped",
              category: cls.primary,
              skip_reason: "category_filter",
              message: `Vídeo ignorado pelo filtro de categorias (classificado como "${categoryLabel(cls.primary)}").`,
            });
            continue;
          }

          // Regra de elegibilidade: importa apenas ensinos (manhã/tarde) e
          // festividades transmitidos ao vivo. Quando o admin escolhe filtros
          // explícitos, a seleção dele define as categorias aceitas.
          const eligibleByCategory = hasFilters
            ? true // já validado pelo filtro de categorias acima
            : cls.primary === "ensino_manha" ||
              cls.primary === "ensino_tarde" ||
              cls.primary === "festividade";

          if (!eligibleByCategory) {
            skipped++;
            tally.add("not_eligible", cls.primary);
            await context.supabase.from("sync_items").insert({
              ...baseLog,
              status: "skipped",
              category: cls.primary,
              skip_reason: "not_eligible",
              message: `Vídeo ignorado: não é ensino nem festividade (classificado como "${categoryLabel(cls.primary)}").`,
            });
            // Remove eventual ensino já importado que não atende mais à regra.
            await context.supabase.from("teachings").delete().eq("youtube_video_id", videoId);
            continue;
          }

          // Ensinos precisam mencionar as palavras-chave configuradas pelo admin
          // e ter sido transmitidos no sábado. Festividades podem ser isentas
          // das duas regras (conforme `import_rules`).
          const requiresSaturday = requiresTeachingChecks(cls.primary, importRules);

          if (
            requiresSaturday &&
            importRules.require_keyword &&
            !matchesTeachingKeyword(originalTitle, rawDescription, importRules)
          ) {
            skipped++;
            tally.add("missing_keyword", cls.primary);
            await context.supabase.from("sync_items").insert({
              ...baseLog,
              status: "skipped",
              category: cls.primary,
              skip_reason: "missing_keyword",
              message: `Vídeo ignorado: título sem as palavras-chave exigidas (${keywordsLabel(importRules)}).`,
            });
            await context.supabase.from("teachings").delete().eq("youtube_video_id", videoId);
            continue;
          }

          if (
            requiresSaturday &&
            importRules.require_saturday &&
            !isSaturdayInSaoPaulo(details.actualStartTime ?? v.snippet?.publishedAt)
          ) {
            skipped++;
            tally.add("not_saturday", cls.primary);
            await context.supabase.from("sync_items").insert({
              ...baseLog,
              status: "skipped",
              category: cls.primary,
              skip_reason: "not_saturday",
              message:
                "Vídeo ignorado: ensino sem transmissão ao vivo no sábado (America/Sao_Paulo).",
            });
            continue;
          }

          const thumb =
            v.snippet?.thumbnails?.maxres?.url ||
            v.snippet?.thumbnails?.high?.url ||
            v.snippet?.thumbnails?.medium?.url;

          let parashaEntry: { id: string; label: string } | undefined;
          if (parsed.parashaNumber && parashaByNumber.has(parsed.parashaNumber))
            parashaEntry = parashaByNumber.get(parsed.parashaNumber);
          else if (parsed.parashaName && parashaByName.has(parsed.parashaName))
            parashaEntry = parashaByName.get(parsed.parashaName);

          // Fallback: tenta pela DESCRIÇÃO do vídeo ("Nª Parashá: NOME"),
          // ignorando URLs e trechos que não falam de parashá.
          const rawDesc = (v.snippet?.description ?? "") as string;
          if (!parashaEntry) {
            parashaEntry =
              findParashaInText(originalTitle, parashaByNumber, parashaByName) ??
              findParashaInText(rawDesc, parashaByNumber, parashaByName);
          }

          const festivitySlug =
            cls.festivity ?? detectFestivitySlug(originalTitle, parashaEntry ? null : rawDesc);
          const festivityId = festivitySlug ? (festivityBySlug.get(festivitySlug) ?? null) : null;

          const dateBR = formatDateBR(effectiveDate);
          const parashaLabel =
            parashaEntry?.label ??
            parsed.parashaName ??
            (festivitySlug ? festivityNameBySlug.get(festivitySlug) : null) ??
            extractTopicLabel(originalTitle) ??
            "Ensino";
          const normalizedTitle = dateBR ? `${dateBR} | ${parashaLabel}` : parashaLabel;

          const row = {
            youtube_video_id: videoId,
            title: normalizedTitle,
            description: v.snippet?.description ?? null,
            thumbnail_url: thumb ?? null,
            published_at: v.snippet?.publishedAt ?? null,
            teaching_date: effectiveDate ?? null,
            turno: parsed.turno ?? null,
            hebrew_year: parsed.hebrewYear ?? null,
            hebrew_date: parsed.hebrewDate ?? null,
            parasha_id: parashaEntry?.id ?? null,
            festivity_id: festivityId,
            needs_review: false,
            raw_metadata: { ...v, liveStreamingDetails: details, originalTitle },
          };

          const { data: existing } = await context.supabase
            .from("teachings")
            .select("id")
            .eq("youtube_video_id", videoId)
            .maybeSingle();

          let status: "inserted" | "updated" | "skipped";
          let message: string;
          if (existing) {
            const { error } = await context.supabase
              .from("teachings")
              .update(row)
              .eq("id", existing.id);
            if (error) throw error;
            updated++;
            status = "updated";
            message = "Metadados atualizados.";
          } else {
            // Dedup: só considera duplicado quando título, descrição E data
            // do ensino coincidem (evita descartar manhã/tarde ou ensinos
            // distintos que compartilham descrição institucional).
            let twinQuery = context.supabase
              .from("teachings")
              .select("id, youtube_video_id")
              .eq("title", normalizedTitle)
              .eq("description", row.description ?? "")
              .neq("youtube_video_id", videoId);
            twinQuery = row.teaching_date
              ? twinQuery.eq("teaching_date", row.teaching_date)
              : twinQuery.is("teaching_date", null);
            const { data: twin } = await twinQuery.maybeSingle();
            if (twin) {
              skipped++;
              status = "skipped";
              message = `Duplicado do vídeo ${twin.youtube_video_id} (mesmo título, descrição e data).`;
            } else {
              const { error } = await context.supabase.from("teachings").insert(row);
              if (error) throw error;
              inserted++;
              status = "inserted";
              message = "Novo ensino importado.";
            }
          }

          if (status === "skipped") tally.add("duplicate", cls.primary);

          await context.supabase.from("sync_items").insert({
            ...baseLog,
            status,
            message,
            skip_reason: status === "skipped" ? "duplicate" : null,
            category: cls.primary,
            parasha_matched: parashaEntry?.label ?? null,
            needs_review: false,
          });
        } catch (e: any) {
          failed++;
          tally.add("error", null);
          await context.supabase.from("sync_items").insert({
            ...baseLog,
            status: "failed",
            skip_reason: "error",
            message: e?.message?.slice(0, 500) ?? "Erro desconhecido.",
          });
        }
      }

      const pagesFetched = (run.pages_fetched ?? 0) + 1;
      const totalVideos = (run.total_videos ?? 0) + enriched.length + skippedNotLive;
      const totalProcessed = (run.processed_count ?? 0) + enriched.length + skippedNotLive;
      const failedTotal = (run.failed_count ?? 0) + failed;
      const insertedTotal = (run.inserted_count ?? 0) + inserted;
      const updatedTotal = (run.updated_count ?? 0) + updated;
      // Lotes consecutivos sem nenhuma novidade (nem inserção nem atualização).
      const noProgressStreak =
        inserted === 0 && updated === 0 ? (run.no_insert_streak ?? 0) + 1 : 0;

      await context.supabase
        .from("sync_runs")
        .update({
          pages_fetched: pagesFetched,
          next_page_token: nextToken,
          total_videos: totalVideos,
          processed_count: totalProcessed,
          inserted_count: insertedTotal,
          updated_count: updatedTotal,
          skipped_count: (run.skipped_count ?? 0) + skipped + skippedNotLive,
          failed_count: failedTotal,
          no_insert_streak: noProgressStreak,
          discard_stats: mergeDiscardStats(run.discard_stats, tally.stats),
          last_progress_at: new Date().toISOString(),
        })
        .eq("id", data.runId);

      // Auditoria automática: impede que a execução siga ativa por erro,
      // por tempo excessivo ou sem necessidade (nada novo a importar).
      const verdict = auditSyncRun({
        startedAt: run.started_at,
        pagesFetched,
        maxPages: run.max_pages,
        insertedCount: insertedTotal,
        updatedCount: updatedTotal,
        failedCount: failedTotal,
        processedCount: totalProcessed,
        noProgressStreak,
        hasNextPage: !!nextToken,
      });

      if (verdict.stop) {
        const { data: updatedRun } = await context.supabase
          .from("sync_runs")
          .select("*")
          .eq("id", data.runId)
          .maybeSingle();
        await finishRun(context, data.runId, updatedRun ?? run, verdict);
        return { done: true, audit: verdict };
      }
      return { done: false };
    } catch (e: any) {
      const { isYoutubeQuotaError } = await import("./youtube-api.server");
      if (isYoutubeQuotaError(e)) {
        await finalize({
          status: "partial",
          error_message: e.message.slice(0, 1000),
          audit_stop_reason: "quota_exhausted",
        });
        throw new Error(e.message);
      }
      const errMsg = e?.message?.slice(0, 1000) ?? "Erro";
      await finalize({ status: "failed", error_message: errMsg, audit_stop_reason: "error" });
      await notifySupportOfError({
        where: "runYoutubeSyncBatch",
        error: e,
        context: { runId: data.runId },
      });
      try {
        const { notifyAdminsSyncAborted } = await import("./email.server");
        await notifyAdminsSyncAborted({
          runId: data.runId,
          reason: `Sincronização manual interrompida por erro: ${errMsg}`,
          code: "error",
          auto: false,
        });
      } catch {
        /* best-effort */
      }
      throw e;
    }
  });

async function finishRun(context: any, runId: string, run: any, verdict?: AuditVerdict) {
  const failed = run.failed_count ?? 0;
  const inserted = run.inserted_count ?? 0;
  const updated = run.updated_count ?? 0;
  const auditFailure = verdict?.code === "failure_rate" || verdict?.code === "timeout";
  const finalStatus = auditFailure
    ? inserted + updated > 0
      ? "partial"
      : "failed"
    : failed === 0
      ? "success"
      : inserted + updated > 0
        ? "partial"
        : "failed";
  await context.supabase
    .from("sync_runs")
    .update({
      status: finalStatus,
      audit_stop_reason: verdict?.code ?? null,
      error_message: auditFailure ? (verdict?.message ?? null) : (run.error_message ?? null),
      finished_at: new Date().toISOString(),
      last_progress_at: new Date().toISOString(),
    })
    .eq("id", runId);
  await logActivity({
    category: "sync",
    action: `sync_${finalStatus}`,
    severity: finalStatus === "failed" ? "error" : failed > 0 || auditFailure ? "warn" : "info",
    actorUserId: context.userId,
    targetType: "sync_run",
    targetId: runId,
    message: `Sincronização: ${inserted} novo(s), ${updated} atualizado(s), ${run.skipped_count ?? 0} ignorado(s), ${failed} falha(s).${verdict?.message ? ` ${verdict.message}` : ""}`,
    metadata: {
      inserted,
      updated,
      skipped: run.skipped_count ?? 0,
      failed,
      total: run.total_videos ?? 0,
      auditStopReason: verdict?.code ?? null,
      discardStats: run.discard_stats ?? {},
    },
  });
  if (auditFailure) {
    const reason = verdict?.message ?? "Auditoria interrompeu a sincronização.";
    try {
      await notifySupportOfError({
        where: "sync-audit",
        error: new Error(reason),
        context: { runId, inserted, updated, failed },
      });
    } catch {
      /* best-effort */
    }
    try {
      const { notifyAdminsSyncAborted } = await import("./email.server");
      await notifyAdminsSyncAborted({
        runId,
        reason,
        code: verdict?.code ?? null,
        auto: false,
        inserted,
        updated,
        failed,
      });
    } catch {
      /* best-effort */
    }
  }
}

const runIdSchema = z.object({ runId: z.string().uuid() });

export const getSyncRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { runId: string }) => runIdSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: run } = await context.supabase
      .from("sync_runs")
      .select("*")
      .eq("id", data.runId)
      .maybeSingle();
    const { data: items } = await context.supabase
      .from("sync_items")
      .select("*")
      .eq("run_id", data.runId)
      .order("processed_at", { ascending: false })
      .limit(500);
    return { run, items: items ?? [] };
  });

export const listSyncRuns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context);
    const { data } = await context.supabase
      .from("sync_runs")
      .select("*")
      .order("started_at", { ascending: false })
      .limit(8);
    return data ?? [];
  });

/**
 * Retoma uma sincronização que falhou/parou, mantendo o mesmo run.
 * Usa o `next_page_token` salvo para continuar de onde parou.
 * Se o run já esgotou seu `max_pages` mas ainda há next_page_token,
 * estende o limite em +20 páginas para permitir continuar.
 */
export const resumeYoutubeSync = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { runId: string }) => z.object({ runId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: run } = await context.supabase
      .from("sync_runs")
      .select("*")
      .eq("id", data.runId)
      .maybeSingle();
    if (!run) throw new Error("Run não encontrada.");
    if (run.status === "running") return { runId: run.id as string };
    if (!run.next_page_token) {
      throw new Error(
        "Este run não tem ponto de retomada (next_page_token vazio). Inicie uma nova sincronização.",
      );
    }
    const patch: {
      status: "running";
      error_message: string | null;
      finished_at: string | null;
      last_progress_at: string;
      max_pages?: number;
    } = {
      status: "running",
      error_message: null,
      finished_at: null,
      last_progress_at: new Date().toISOString(),
    };
    if ((run.pages_fetched ?? 0) >= (run.max_pages ?? 0)) {
      patch.max_pages = (run.pages_fetched ?? 0) + 20;
    }
    await context.supabase.from("sync_runs").update(patch).eq("id", data.runId);
    await logActivity({
      category: "sync",
      action: "sync_resumed",
      actorUserId: context.userId,
      targetType: "sync_run",
      targetId: data.runId,
      message: `Sincronização retomada a partir do último next_page_token.`,
    });
    return { runId: run.id as string };
  });

/** Interrompe manualmente uma sincronização em andamento. */
export const cancelYoutubeSync = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { runId: string }) => z.object({ runId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: run } = await context.supabase
      .from("sync_runs")
      .select("status")
      .eq("id", data.runId)
      .maybeSingle();
    if (!run) throw new Error("Run não encontrada.");
    if (run.status !== "running") return { ok: true, alreadyStopped: true };
    await context.supabase
      .from("sync_runs")
      .update({
        status: "failed",
        finished_at: new Date().toISOString(),
        last_progress_at: new Date().toISOString(),
        error_message: "Sincronização interrompida manualmente pelo administrador.",
      })
      .eq("id", data.runId);
    await logActivity({
      category: "sync",
      action: "sync_cancelled",
      severity: "warn",
      actorUserId: context.userId,
      targetType: "sync_run",
      targetId: data.runId,
      message: "Sincronização interrompida manualmente.",
    });
    return { ok: true };
  });

/** Recusa (exclui) um cadastro pendente: remove profile, roles e conta auth. */
export const rejectPendingSignup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    if (data.userId === context.userId) {
      throw new Error("Você não pode recusar seu próprio cadastro.");
    }
    const { data: target } = await context.supabase
      .from("profiles")
      .select("full_name, approved")
      .eq("id", data.userId)
      .maybeSingle();
    if (target?.approved) {
      throw new Error("Este cadastro já foi aprovado. Use 'Bloquear' em Membros.");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.userId);
    if (error) throw error;
    await logActivity({
      category: "admin",
      action: "reject_member",
      severity: "warn",
      actorUserId: context.userId,
      targetType: "user",
      targetId: data.userId,
      message: `Recusou cadastro de ${target?.full_name ?? data.userId}`,
    });
    return { ok: true };
  });

export const listAdminTeachings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { search?: string; limit?: number; offset?: number }) =>
    z
      .object({
        search: z.string().optional(),
        limit: z.number().int().default(50),
        offset: z.number().int().default(0),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    let q = context.supabase
      .from("teachings")
      .select(
        "id, title, custom_title, teaching_date, turno, hebrew_year, parashiot(name_translit, number), speakers(name), festivities(name_pt)",
        { count: "exact" },
      )
      .order("teaching_date", { ascending: false, nullsFirst: false })
      .range(data.offset, data.offset + data.limit - 1);
    if (data.search && data.search.trim().length > 0) {
      const clause = buildIlikeOr(["title", "custom_title"], data.search);
      if (clause) q = q.or(clause);
    }
    const { data: rows, count, error } = await q;
    if (error) throw error;
    return { rows: rows ?? [], count: count ?? 0 };
  });

const deleteTeachingSchema = z.object({ id: z.string().uuid() });
export const deleteTeaching = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => deleteTeachingSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: t } = await context.supabase
      .from("teachings")
      .select("title")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await context.supabase.from("teachings").delete().eq("id", data.id);
    if (error) throw error;
    await logActivity({
      category: "admin",
      action: "delete_teaching",
      actorUserId: context.userId,
      targetType: "teaching",
      targetId: data.id,
      message: `Removeu ensino: ${t?.title ?? data.id}`,
    });
    return { ok: true };
  });

export const listMembers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context);
    const { data: profiles, error } = await context.supabase
      .from("profiles")
      .select(
        "id, full_name, avatar_url, created_at, approved, approved_at, cpf, birth_date, membership_status, rejected_at, rejection_reason",
      )
      .order("created_at", { ascending: false });
    if (error) throw error;
    const { data: roles } = await context.supabase.from("user_roles").select("user_id, role");
    const rolesByUser = new Map<string, string[]>();
    (roles ?? []).forEach((r: any) => {
      if (!rolesByUser.has(r.user_id)) rolesByUser.set(r.user_id, []);
      rolesByUser.get(r.user_id)!.push(r.role);
    });

    // E-mails vêm do serviço de autenticação (somente para administradores).
    const emailById = new Map<string, string>();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      for (let page = 1; page <= 20; page++) {
        const { data: list } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 200 });
        const users = list?.users ?? [];
        for (const u of users) if (u.email) emailById.set(u.id, u.email);
        if (users.length < 200) break;
      }
    } catch {
      /* best-effort */
    }

    const { signAvatarUrl } = await import("./avatar.server");
    return await Promise.all(
      (profiles ?? []).map(async (p) => ({
        ...p,
        avatar_url: await signAvatarUrl(p.avatar_url),
        email: emailById.get(p.id) ?? null,
        roles: rolesByUser.get(p.id) ?? [],
      })),
    );
  });

// Dados pessoais (nome, CPF e data de nascimento) NÃO são editáveis nesta
// plataforma, nem por administradores: a fonte da verdade é a Gestão de
// cadastros e os valores chegam pela sincronização automática.
const adminUserPatchSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().trim().email().max(255).optional(),
  password: z.string().min(8).max(72).optional(),
});

/** Permite que qualquer administrador altere o e-mail de acesso e a senha. */
export const adminUpdateUserAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof adminUserPatchSchema>) => adminUserPatchSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const changed: string[] = [];

    const authPatch: { email?: string; password?: string; email_confirm?: boolean } = {};
    if (data.email) {
      authPatch.email = data.email;
      authPatch.email_confirm = true;
      changed.push("e-mail");
    }
    if (data.password) {
      authPatch.password = data.password;
      changed.push("senha");
    }
    if (Object.keys(authPatch).length > 0) {
      const { error } = await supabaseAdmin.auth.admin.updateUserById(data.userId, authPatch);
      if (error) {
        const { isAuthEmailConflictError } = await import("./member-sync.server");
        if (data.email && isAuthEmailConflictError(error.message)) {
          throw new Error(
            "Este e-mail já está em uso por outro membro. Escolha um e-mail diferente.",
          );
        }
        throw new Error(error.message);
      }
    }

    if (changed.length === 0) return { ok: true, changed };

    await logActivity({
      category: "admin",
      action: "update_user_account",
      severity: "warn",
      actorUserId: context.userId,
      targetType: "user",
      targetId: data.userId,
      message: `Alterou ${changed.join(", ")} do usuário ${data.userId}`,
    });

    // Reflete a alteração na plataforma de gestão de cadastros (best-effort).
    const { pushMemberToGestao } = await import("./member-sync.server");
    await pushMemberToGestao({ action: "member.updated", userId: data.userId });

    return { ok: true, changed };
  });

const queueSchema = z.object({
  status: z.enum(["pending", "approved", "rejected", "all"]).default("pending"),
});

/**
 * Fila de cadastros por situação (em análise / aprovados / reprovados / todos).
 * Cadastros reprovados NUNCA são excluídos: permanecem aqui como histórico.
 */
export const listMembershipQueue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof queueSchema>) => queueSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    let query = context.supabase
      .from("profiles")
      .select(
        "id, full_name, avatar_url, created_at, updated_at, cpf, birth_date, membership_status, approved, approved_at, approved_by, rejected_at, rejected_by, rejection_reason, access_block_reason, access_block_detail, external_status, external_member_id",
      )
      .order("created_at", { ascending: false });
    if (data.status !== "all") query = query.eq("membership_status", data.status);
    const { data: rows, error } = await query;
    if (error) throw error;

    // E-mails e nomes dos administradores responsáveis vêm do serviço de auth.
    const emailById = new Map<string, string>();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      for (let page = 1; page <= 20; page++) {
        const { data: list } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 200 });
        const users = list?.users ?? [];
        for (const u of users) if (u.email) emailById.set(u.id, u.email);
        if (users.length < 200) break;
      }
    } catch {
      /* best-effort */
    }

    const actorIds = Array.from(
      new Set((rows ?? []).flatMap((r: any) => [r.approved_by, r.rejected_by].filter(Boolean))),
    ) as string[];
    const actorNames = new Map<string, string>();
    if (actorIds.length > 0) {
      const { data: actors } = await context.supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", actorIds);
      (actors ?? []).forEach((a: any) => actorNames.set(a.id, a.full_name ?? a.id));
    }

    const { signAvatarUrl } = await import("./avatar.server");
    return await Promise.all(
      (rows ?? []).map(async (p: any) => ({
        ...p,
        avatar_url: await signAvatarUrl(p.avatar_url),
        email: emailById.get(p.id) ?? null,
        approved_by_name: p.approved_by ? (actorNames.get(p.approved_by) ?? null) : null,
        rejected_by_name: p.rejected_by ? (actorNames.get(p.rejected_by) ?? null) : null,
      })),
    );
  });

/** Contadores por situação, usados nas abas e no menu do admin. */
export const membershipStatusCounts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context);
    const count = async (status: string) => {
      const { count: c } = await context.supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("membership_status", status);
      return c ?? 0;
    };
    const [pending, approved, rejected] = await Promise.all([
      count("pending"),
      count("approved"),
      count("rejected"),
    ]);
    return { pending, approved, rejected, all: pending + approved + rejected };
  });

/** Histórico de mudanças de situação de um cadastro. */
export const getMemberStatusHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { userId: string }) => z.object({ userId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: rows, error } = await context.supabase
      .from("profile_status_events")
      .select("id, from_status, to_status, reason, source, actor_user_id, created_at")
      .eq("profile_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw error;
    return rows ?? [];
  });

const statusActionSchema = z
  .object({
    userId: z.string().uuid(),
    status: z.enum(["pending", "approved", "rejected"]),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.status !== "rejected" || (v.reason?.length ?? 0) >= 3, {
    message: "Informe o motivo da reprovação.",
    path: ["reason"],
  });

/**
 * Define a situação do cadastro (aprovar, reprovar ou devolver para análise).
 * A escrita é feita via service_role e registra histórico + log administrativo.
 */
export const setMembershipStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof statusActionSchema>) => statusActionSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    if (data.userId === context.userId && data.status !== "approved") {
      throw new Error("Você não pode reprovar ou suspender o seu próprio cadastro.");
    }

    const { applyMembershipStatus } = await import("./membership.server");
    const { from } = await applyMembershipStatus({
      userId: data.userId,
      status: data.status,
      reason: data.reason ?? null,
      actorUserId: context.userId,
      source: "admin",
    });

    const { data: target } = await context.supabase
      .from("profiles")
      .select("full_name")
      .eq("id", data.userId)
      .maybeSingle();
    const label =
      data.status === "approved"
        ? "Aprovou"
        : data.status === "rejected"
          ? "Reprovou"
          : "Devolveu para análise";
    await logActivity({
      category: "admin",
      action:
        data.status === "approved"
          ? "approve_member"
          : data.status === "rejected"
            ? "reject_member"
            : "reset_member_review",
      severity: data.status === "rejected" ? "warn" : "info",
      actorUserId: context.userId,
      targetType: "user",
      targetId: data.userId,
      message: `${label} o cadastro de ${target?.full_name ?? data.userId}${
        from ? ` (situação anterior: ${from})` : ""
      }${data.reason ? ` — motivo: ${data.reason}` : ""}`,
    });

    // E-mail de aprovação (best-effort). Reprovação não notifica o usuário.
    if (data.status === "approved") {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: authUser } = await supabaseAdmin.auth.admin.getUserById(data.userId);
        const emailAddr = authUser?.user?.email;
        if (emailAddr) {
          const { sendApprovalEmail } = await import("./email.server");
          await sendApprovalEmail({
            to: emailAddr,
            fullName: target?.full_name,
            actorLabel: context.userId,
          });
        }
      } catch (e) {
        await notifySupportOfError({
          where: "setMembershipStatus:sendEmail",
          error: e,
          context: { userId: data.userId },
        });
      }
    }

    // Reflete a decisão na Gestão de cadastros (best-effort).
    const { pushMemberToGestao } = await import("./member-sync.server");
    await pushMemberToGestao({
      action: data.status === "approved" ? "member.approved" : "member.revoked",
      userId: data.userId,
    });

    return { ok: true };
  });

const roleActionSchema = z.object({ userId: z.string().uuid(), makeAdmin: z.boolean() });

export const setAdminRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof roleActionSchema>) => roleActionSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    if (data.userId === context.userId && !data.makeAdmin) {
      throw new Error("Você não pode remover seu próprio acesso admin.");
    }
    if (data.makeAdmin) {
      const { error } = await context.supabase
        .from("user_roles")
        .upsert({ user_id: data.userId, role: "admin" }, { onConflict: "user_id,role" });
      if (error) throw error;
      // Garantir aprovação ao promover (decisão administrativa explícita).
      await context.supabase
        .from("profiles")
        .update({
          approved: true,
          approved_at: new Date().toISOString(),
          approved_by: context.userId,
          membership_status: "approved",
        })
        .eq("id", data.userId);
    } else {
      const { error } = await context.supabase
        .from("user_roles")
        .delete()
        .eq("user_id", data.userId)
        .eq("role", "admin");
      if (error) throw error;
    }
    await logActivity({
      category: "admin",
      action: data.makeAdmin ? "grant_admin" : "revoke_admin",
      severity: "warn",
      actorUserId: context.userId,
      targetType: "user",
      targetId: data.userId,
      message: `${data.makeAdmin ? "Concedeu" : "Removeu"} acesso admin para usuário ${data.userId}`,
    });
    return { ok: true };
  });

const speakerSchema = z.object({ name: z.string().min(1) });
export const createSpeaker = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { name: string }) => speakerSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { data: row, error } = await context.supabase
      .from("speakers")
      .insert({ name: data.name })
      .select()
      .single();
    if (error) throw error;
    return row;
  });

/* ----------------- Inserção manual de vídeo (admin) ----------------- */

function extractYoutubeId(input: string): string | null {
  const t = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(t)) return t;
  try {
    const url = new URL(t);
    const v = url.searchParams.get("v");
    if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;
    const m = url.pathname.match(/(?:shorts|live|embed)\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    if (url.hostname.endsWith("youtu.be")) {
      const id = url.pathname.slice(1);
      if (/^[A-Za-z0-9_-]{11}$/.test(id)) return id;
    }
  } catch {
    /* ignore */
  }
  return null;
}

async function fetchYoutubeMeta(
  videoId: string,
): Promise<{ title?: string; thumbnail?: string; publishedAt?: string; duration?: number } | null> {
  const { getYoutubeApiKey } = await import("./platform-secrets.server");
  const key = await getYoutubeApiKey();
  if (!key) return null;
  try {
    const { youtubeList } = await import("./youtube-api.server");
    const json = await youtubeList<{
      items?: Array<{
        snippet?: {
          title?: string;
          publishedAt?: string;
          thumbnails?: {
            maxres?: { url?: string };
            high?: { url?: string };
            medium?: { url?: string };
          };
        };
        contentDetails?: { duration?: string };
      }>;
    }>("videos", { part: "snippet,contentDetails", id: videoId }, { apiKey: key });
    const item = json?.items?.[0];
    if (!item) return null;
    const iso = item.contentDetails?.duration as string | undefined;
    let duration = 0;
    if (iso) {
      const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (m)
        duration =
          parseInt(m[1] ?? "0") * 3600 + parseInt(m[2] ?? "0") * 60 + parseInt(m[3] ?? "0");
    }
    return {
      title: item.snippet?.title,
      thumbnail:
        item.snippet?.thumbnails?.maxres?.url ??
        item.snippet?.thumbnails?.high?.url ??
        item.snippet?.thumbnails?.medium?.url,
      publishedAt: item.snippet?.publishedAt,
      duration,
    };
  } catch {
    return null;
  }
}

const manualTeachingSchema = z.object({
  youtubeUrl: z.string().trim().min(3).max(500),
  teachingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inválida"),
  parashaId: z.string().uuid().nullable().optional(),
  festivityId: z.string().uuid().nullable().optional(),
  turno: z.enum(["manha", "tarde"]).nullable().optional(),
  customTitle: z.string().trim().max(200).nullable().optional(),
});

export const createManualTeaching = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof manualTeachingSchema>) => manualTeachingSchema.parse(data))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const videoId = extractYoutubeId(data.youtubeUrl);
    if (!videoId) throw new Error("Link do YouTube inválido — cole a URL completa do vídeo.");

    const { data: existing } = await context.supabase
      .from("teachings")
      .select("id")
      .eq("youtube_video_id", videoId)
      .maybeSingle();
    if (existing) throw new Error("Este vídeo já está cadastrado na plataforma.");

    if (!data.parashaId && !data.festivityId) {
      throw new Error("Selecione uma Parashá ou uma festividade.");
    }

    const meta = await fetchYoutubeMeta(videoId);

    let parashaLabel: string | null = null;
    if (data.parashaId) {
      const { data: p } = await context.supabase
        .from("parashiot")
        .select("name_translit")
        .eq("id", data.parashaId)
        .maybeSingle();
      parashaLabel = p?.name_translit ?? null;
    } else if (data.festivityId) {
      const { data: f } = await context.supabase
        .from("festivities")
        .select("name_pt")
        .eq("id", data.festivityId)
        .maybeSingle();
      parashaLabel = f?.name_pt ?? null;
    }

    const [y, m, dd] = data.teachingDate.split("-");
    const displayTitle = parashaLabel ? `${dd}/${m}/${y} | ${parashaLabel}` : `${dd}/${m}/${y}`;

    const { data: inserted, error } = await context.supabase
      .from("teachings")
      .insert({
        youtube_video_id: videoId,
        title: displayTitle,
        custom_title: data.customTitle?.trim() || null,
        thumbnail_url: meta?.thumbnail ?? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        duration_seconds: meta?.duration ?? null,
        published_at: meta?.publishedAt ?? new Date(data.teachingDate).toISOString(),
        teaching_date: data.teachingDate,
        turno: data.turno ?? null,
        parasha_id: data.parashaId ?? null,
        festivity_id: data.festivityId ?? null,
        needs_review: false,
        added_by: context.userId,
        source: "manual",
        raw_metadata: { originalTitle: meta?.title ?? null, addedManually: true } as any,
      })
      .select("id")
      .single();
    if (error) throw error;

    await logActivity({
      category: "admin",
      action: "manual_teaching_created",
      actorUserId: context.userId,
      targetType: "teaching",
      targetId: inserted.id,
      message: `Adicionou vídeo manualmente: ${displayTitle}`,
      metadata: { videoId },
    });

    return { id: inserted.id, title: displayTitle };
  });

/* ----------------- Dashboard admin ----------------- */

export const getAdminDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [
      { count: totalUsers },
      { count: approvedUsers },
      { count: pendingUsers },
      { count: totalTeachings },
      { count: totalViews },
      { count: totalFavorites },
      { count: totalComments },
    ] = await Promise.all([
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }),
      supabaseAdmin
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("approved", true),
      supabaseAdmin
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("approved", false),
      supabaseAdmin.from("teachings").select("id", { count: "exact", head: true }),
      supabaseAdmin
        .from("user_watch_progress")
        .select("teaching_id", { count: "exact", head: true }),
      supabaseAdmin.from("user_favorites").select("teaching_id", { count: "exact", head: true }),
      supabaseAdmin.from("teaching_comments").select("id", { count: "exact", head: true }),
    ]);

    // Top ensinos mais assistidos
    const { data: watchRows } = await supabaseAdmin
      .from("user_watch_progress")
      .select("teaching_id, teachings(id, title, teaching_date, parashiot(name_translit))");
    const teachingCount = new Map<
      string,
      { title: string; date: string | null; parasha: string | null; views: number }
    >();
    (watchRows ?? []).forEach((r: any) => {
      if (!r.teachings) return;
      const cur = teachingCount.get(r.teaching_id) ?? {
        title: r.teachings.title,
        date: r.teachings.teaching_date,
        parasha: r.teachings.parashiot?.name_translit ?? null,
        views: 0,
      };
      cur.views++;
      teachingCount.set(r.teaching_id, cur);
    });
    const topTeachings = Array.from(teachingCount.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Top usuários mais ativos (por ensinos assistidos únicos)
    const userCount = new Map<string, number>();
    (watchRows ?? []).forEach((r: any) => {
      // reconstitute user from a second query below
    });
    const { data: perUser } = await supabaseAdmin.from("user_watch_progress").select("user_id");
    (perUser ?? []).forEach((r: any) => {
      userCount.set(r.user_id, (userCount.get(r.user_id) ?? 0) + 1);
    });
    const topUserIds = Array.from(userCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    const { data: topProfiles } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, avatar_url")
      .in("id", topUserIds.map(([id]) => id).concat(["00000000-0000-0000-0000-000000000000"]));
    const profileById = new Map((topProfiles ?? []).map((p: any) => [p.id, p]));
    const { signAvatarUrl } = await import("./avatar.server");
    const topUsers = await Promise.all(
      topUserIds.map(async ([id, count]) => ({
        id,
        count,
        name: profileById.get(id)?.full_name ?? "Membro",
        avatar: await signAvatarUrl(profileById.get(id)?.avatar_url ?? null),
      })),
    );

    // Atividade recente (últimos 30 dias)
    const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
    const { count: newUsersLast30 } = await supabaseAdmin
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since);
    const { count: viewsLast30 } = await supabaseAdmin
      .from("user_watch_progress")
      .select("teaching_id", { count: "exact", head: true })
      .gte("updated_at", since);

    return {
      counts: {
        totalUsers: totalUsers ?? 0,
        approvedUsers: approvedUsers ?? 0,
        pendingUsers: pendingUsers ?? 0,
        totalTeachings: totalTeachings ?? 0,
        totalViews: totalViews ?? 0,
        totalFavorites: totalFavorites ?? 0,
        totalComments: totalComments ?? 0,
        newUsersLast30: newUsersLast30 ?? 0,
        viewsLast30: viewsLast30 ?? 0,
      },
      topTeachings,
      topUsers,
    };
  });
